import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ChatDocumentCard } from '../models/chat-message.model';
import * as ExcelJS from 'exceljs';

interface DrtFieldNode {
    description?: string;
    is_simple_field?: boolean;
    is_table?: boolean;
    table_columns?: Record<string, string>;
    subfields?: Record<string, DrtFieldNode>;
}

interface SqlContextField {
    field_name: string;
    description: string;
}

interface DrtSchema {
    drt_fields: Record<string, DrtFieldNode>;
    sql_context_fields: SqlContextField[];
}

interface TableColumn {
    keyPath: string[];
    topLabel: string;
    subLabel: string;
    leafLabel: string;
    description?: string;
}

interface HeaderGroup {
    label: string;
    span: number;
}

interface CitationLink {
    label: string;
    url: string;
}

interface PastEwoSuggestion {
    value: string;
    documents: CitationLink[];
}

interface ValidationBadge {
    label: string;
    tone: 'info' | 'warning' | 'error' | 'neutral';
    detail: string;
}

interface TableRow {
    documentTitle: string;
    values: string[];
    aiReasons: string[];
    citations: CitationLink[][];
    conflicts: string[];
    validationBadges: ValidationBadge[][];
    pastEwoSuggestions: PastEwoSuggestion[][];
}

interface VerticalRow {
    section: string;
    subSection: string;
    fieldName: string;
    value: string;
    citations: CitationLink[];
    aiReason: string;
    conflict: string;
    validationBadges: ValidationBadge[];
    pastEwoSuggestions: PastEwoSuggestion[];
    // rowspan / show flags for merged cells
    showSection: boolean;
    sectionRowspan: number;
    showSubSection: boolean;
    subSectionRowspan: number;
}

const VALIDATION_META_KEYS = new Set([
    'origin', 'origins', 'validation_status', 'validation_issues', 'verification',
    'normalization', 'requested_value', 'inactive_requested_value'
]);

const DYNAMIC_META_KEYS = new Set([
    ...VALIDATION_META_KEYS,
    'value',
    'reference',
    'updated_reference',
    'ai reason',
    'ai_reason',
    'citations',
    'dropdown_opts',
    'past_ewo_suggestions'
]);

const KEY_ALIASES: Record<string, string[]> = {
    pwhtpostweldheattreatment: ['postweldheattreatmentpwht'],
    environmentalconditionsrestrictions: ['environmentalscc'],
    applicable: ['isrequired'],
    temperaturef: ['temperaturedegreefahrenheit', 'temperature'],
    durationhours: ['duration'],
    minpreheatf: ['minpreheatdegreefahrenheit'],
    maxinterpassf: ['maximuminterpasstemperaturedegreefahrenheit'],
    hydrotestpsig: ['hydrotestpressurepsig'],
    ndenotes: ['notes']
};

const PROCESS_INFO_SECTION_KEYS = ['Process Information', 'process_info'];

const DRT_TABLE_SCHEMA: DrtSchema = {
    drt_fields: {
        Bakeout_Information: {
            description: 'Information about bakeout procedure',
            subfields: {
                'Temperature_(°F)': { description: 'value°F' },
                Type: { description: 'e.g. Hydrogen, Sulphur etc.' },
                'Duration_(hours)': { description: 'value in hours' },
                Notes: { description: 'Additional notes or observations related to bakeout if any, else null' }
            }
        },
        Welding_information: {
            description: 'Information about welding procedures and materials',
            subfields: {
                Metallurgy: { description: 'i.e, material name, pipe spec number or p number' },
                Overlay_information: { description: 'Paste all the text and values in overlay field in welding' },
                Welding_procedures: {
                    description: 'Return a table with these columns if exist',
                    is_table: true,
                    table_columns: {
                        Type_of_weld: 'Provide if available; e.g., butt weld, fillet weld, etc. Repeat weld type for each procedure if multiple procedures of same weld type are listed',
                        'Weld procedure': 'list of procedures in table (i.e, CVX code) for each weld type if available',
                        'Welding method': 'List of methods corresponding to each procedure in table (i.e, SMAW, GTAW BW >2")',
                        'Filler metal': 'List of filler metals corresponding to each procedure in table'
                    }
                }
            }
        },
        'NDE (Non-Destructive Examination)': {
            description: 'Non-destructive examination requirements',
            subfields: {
                'Hydrotest (psig)': { description: 'value (e.g, range 200-3000 psig)' },
                'RT/PAUT (%)': { description: 'value%' },
                'PT/MT (%)': { description: 'value%' },
                'Ferrite Test': { description: 'Yes or No or null' },
                'Hardness Test(BHN)': { description: 'value%BHN' },
                'Visual Test': { description: 'value%' },
                'NDE Notes': { description: 'PT/MT, hydrotest related notes: Additional notes or observations related to NDE if any, else null' },
                'Hydrotest Waived': { description: 'value_found_or_null' },
                'Welds with waived Hydro': { description: 'value_found_or_null' },
                'NDE In-Lieu of Hydro': { description: 'value_found_or_null' },
                'Hydrotest waiver notes': { description: 'value_found_or_null' }
            }
        },
        'PWHT (Post Weld Heat Treatment)': {
            description: 'Post weld heat treatment requirements',
            subfields: {
                Applicable: { description: 'Yes or No or null (No if temperature is not specified)' },
                'Temperature (°F)': { description: 'value°F' },
                'Duration (hours)': { description: 'value hours' }
            }
        },
        'Preheat & Interpass Temperatures': {
            description: 'Temperature requirements for welding',
            subfields: {
                Applicable: { description: 'Yes or No or null (No if values are not specified)' },
                'Min Preheat (°F)': { description: 'value°F' },
                'Max Interpass (°F)': { description: 'value°F' }
            }
        },
        'DHT / ISR': {
            description: 'Heat treatment information',
            subfields: {
                'Dehydrogenation Heat Treatment (DHT)': { description: 'value_found_or_null' },
                'Intermediate Stress Relief (ISR)': { description: 'value_found_or_null' }
            }
        },
        'Service/Process': {
            description: 'Can be found in Process Information and technical specifications tables,e.g, Sour water, Seal oil, Amine, Gasoline, Steam etc.',
            is_simple_field: true
        },
        'API pipe class': {
            description: 'Can be found in Process Information and technical specifications tables, (e.g, class 1, 2, 3 or 4)',
            is_simple_field: true
        },
        Sour: {
            description: 'Yes or No or null',
            is_simple_field: true
        },
        'Environmental Conditions / Restrictions': {
            description: 'e.g, Caustic T>140 & <30%wt%, Caustic T>110 & >30%wt%, Amine, Ammonia, Carbonate, Wet H2S, Sulfide, Chloride, CO-CO2, Ethanol',
            is_simple_field: true
        }
    },
    sql_context_fields: [
        {
            field_name: 'Scope_Summary',
            description: 'Detailed scope of work description from Salesforce - provides high-level overview of work to be performed'
        },
        {
            field_name: 'Title_EWO',
            description: 'Job Title from DEWIT - provides overview of work'
        },
        {
            field_name: 'IMPACT_Description',
            description: 'Description of the work order from DEWIT'
        },
        {
            field_name: 'Job_Description',
            description: 'Job description from DEWIT'
        },
        {
            field_name: 'TA_scope_description',
            description: 'Facility area from Salesforce'
        },
        {
            field_name: 'Justification',
            description: 'Turnaround justification'
        }
    ]
};

// Lists backed by DRT_clean4.json arrive from the backend as `excel_dropdowns` (build_excel_dropdowns).
// Only the lists that do not exist in the template JSON are kept here.
const DRT_SHEET1_LOCAL_LISTS: Record<string, string[]> = {
    pNumber: ['P1 (CS)', 'P3 (C-1/2Mo)', 'P4 (1 1/4Cr-1/2Mo)', 'P5A (2 1/4Cr-1Mo)', 'P5B (5Cr-1Mo)', 'P5B (9Cr-1Mo)', 'P8 (Austenitic SS)', 'P10H (Duplex SS)', 'P34 (CuNi Alloy)', 'P42 (Monel)', 'P43 (NiCrFe Alloy)', 'P44', 'P45'],
    visual: ['100%'],
    hardness: ['0.1', '1', '100% - local PWHT, 10% - furnace PWHT'],
    heatSource: ['N/A', 'Electric', 'Steam', 'Oil'],
};

const DRT_SHEET1_DROPDOWN_CELLS: Array<[string, string]> = [
    ['B2', 'service'], ['D2', 'pipeClass'], ['B3', 'scc'], ['D3', 'yesNo'],
    ['F2', 'bakeoutTemp'], ['H2', 'bakeoutType'], ['F3', 'bakeoutDuration'],
    ['F20', 'pwhtTemp'], ['H20', 'pwhtDuration'],
    ['B8', 'pNumber'], ['D8', 'pNumber'],
    ['B9', 'preheat'], ['D9', 'interpass'],
    ['B10', 'preheatInterruption'], ['C10', 'preheatInterruption'], ['D10', 'preheatInterruption'],
    ['B11', 'preheatInterruption'], ['C11', 'preheatInterruption'], ['D11', 'preheatInterruption'],
    ['B23', 'pmi'], ['B25', 'ptmt'],
    ['B26', 'visual'], ['C26', 'visual'], ['D26', 'visual'],
    ['B28', 'bhn'], ['C28', 'hardness'],
    ['B32', 'yesNo'], ['C32', 'yesNo'], ['D32', 'yesNo'],
    ['G33', 'heatSource'], ['G34', 'heatSource'],
    ['H49', 'codes']
];

/**
 * The template's validations use INDIRECT() on tables that only exist in the external EWO workbook,
 * so they break once exported. Replace them with explicit lists stored on a hidden "Lists" sheet.
 */
function applyDrtSheet1Dropdowns(wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet, backendLists: Record<string, string[]> = {}): void {
    const model = (ws as any).dataValidations.model as Record<string, any>;
    Object.keys(model).forEach(addr => {
        const f = model[addr]?.formulae?.[0];
        if (typeof f === 'string' && /INDIRECT/i.test(f)) delete model[addr];
    });

    const lists = wb.addWorksheet('Lists', { state: 'hidden' });
    const ranges: Record<string, string> = {};
    Object.entries({ ...DRT_SHEET1_LOCAL_LISTS, ...backendLists }).forEach(([key, values], i) => {
        const col = i + 1;
        values.forEach((v, r) => { lists.getCell(r + 1, col).value = v; });
        const letter = lists.getColumn(col).letter;
        ranges[key] = `Lists!$${letter}$1:$${letter}$${values.length}`;
    });

    // 'warning' lets users keep AI-filled values that are not in the list instead of blocking them.
    DRT_SHEET1_DROPDOWN_CELLS.filter(([, key]) => ranges[key]).forEach(([addr, key]) => {
        ws.getCell(addr).dataValidation = {
            type: 'list',
            allowBlank: true,
            showErrorMessage: true,
            errorStyle: 'warning',
            formulae: [ranges[key]]
        };
    });
}

@Component({
    selector: 'app-drt-parameters-table',
    templateUrl: './drt-parameters-table.component.html',
    styleUrls: ['./drt-parameters-table.component.scss'],
    standalone: false
})
export class DrtParametersTableComponent implements OnChanges, OnDestroy {
    @Input() documents: ChatDocumentCard[] = [];
    @Input() tableTitle: string = 'Comparison DRT Table';
    @Input() initiallyExpanded: boolean = false;
    @Input() dynamicColumns: boolean = false;

    public columns: TableColumn[] = [];
    public rows: TableRow[] = [];
    public topHeaderGroups: HeaderGroup[] = [];
    public subHeaderGroups: HeaderGroup[] = [];

    public allColumns: TableColumn[] = [];
    public allRows: TableRow[] = [];
    public allTopHeaderGroups: HeaderGroup[] = [];
    public allSubHeaderGroups: HeaderGroup[] = [];

    public isExpanded: boolean = false;
    public isModalOpen: boolean = false;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['documents']) {
            if (this.dynamicColumns) {
                this.buildTableDynamic();
            } else {
                this.buildTable();
            }
            this.isExpanded = this.initiallyExpanded;
            this.isModalOpen = false;
        }

        if (changes['initiallyExpanded'] && !changes['documents']) {
            this.isExpanded = this.initiallyExpanded;
        }
    }

    public ngOnDestroy(): void {
        document.body.classList.remove('drt-modal-open');
    }

    public hasRenderableData(): boolean {
        return this.rows.length > 0 && this.columns.length > 0;
    }

    public get verticalRows(): VerticalRow[] {
        // dynamicColumns=true  → single DRT view: use allColumns/allRows (unfiltered)
        //                         so empty-array columns (e.g. Fillet Welds) are shown
        // dynamicColumns=false → comparison view: use columns/rows (pre-filtered,
        //                         same as before — no change to comparison behaviour)
        if (this.dynamicColumns) {
            return this.buildVerticalRows(this.allColumns, this.allRows, true);
        }
        return this.buildVerticalRows(this.columns, this.rows, false);
    }

    public get allVerticalRows(): VerticalRow[] {
        if (this.dynamicColumns) {
            return this.buildVerticalRows(this.allColumns, this.allRows, true);
        }
        return this.buildVerticalRows(this.allColumns, this.allRows, false);
    }

    private buildVerticalRows(cols: TableColumn[], tableRows: TableRow[], showEmpty: boolean): VerticalRow[] {
        if (!cols.length || !tableRows.length) { return []; }
        const row = tableRows[0];

        // ── 1. Collect rows ───────────────────────────────────────────────────
        // topLabel  → Column 1 (Section)    — rowspan-merged
        // subLabel  → Column 2 (Sub-section) — rowspan-merged
        // leafLabel → Column 3 (Field)       — blank '' for 2-level columns
        //
        // showEmpty=true  (single DRT):  every column in cols is rendered; '--' shown as '-'
        // showEmpty=false (comparison):  columns with value '--' are skipped (old behaviour)
        const raw: VerticalRow[] = [];
        cols.forEach((col, i) => {
            const value = row.values[i];
            if (!showEmpty && (!value || value === '--') && !row.validationBadges[i]?.length) { return; }
            const displayValue = (!value || value === '--') ? '-' : value;
            raw.push({
                section:           col.topLabel,
                subSection:        col.subLabel === 'Details' ? '' : col.subLabel,
                fieldName:         col.leafLabel,
                value:             displayValue,
                citations:         row.citations[i] || [],
                aiReason:          row.aiReasons[i] || '',
                conflict:          row.conflicts[i] || '',
                validationBadges:  row.validationBadges[i] || [],
                pastEwoSuggestions: row.pastEwoSuggestions[i] || [],
                showSection:       false,
                sectionRowspan:    1,
                showSubSection:    false,
                subSectionRowspan: 1
            });
        });

        // ── 2. Compute section rowspans (group consecutive same-section rows) ─
        let sIdx = 0;
        while (sIdx < raw.length) {
            const sec = raw[sIdx].section;
            let span = 1;
            while (sIdx + span < raw.length && raw[sIdx + span].section === sec) { span++; }
            raw[sIdx].showSection    = true;
            raw[sIdx].sectionRowspan = span;
            for (let k = sIdx + 1; k < sIdx + span; k++) {
                raw[k].showSection    = false;
                raw[k].sectionRowspan = 0;
            }
            sIdx += span;
        }

        // ── 3. Compute sub-section rowspans within each section group ─────────
        let subIdx = 0;
        while (subIdx < raw.length) {
            const sec = raw[subIdx].section;
            const sub = raw[subIdx].subSection;
            let span = 1;
            while (
                subIdx + span < raw.length &&
                raw[subIdx + span].section    === sec &&
                raw[subIdx + span].subSection === sub
            ) { span++; }
            raw[subIdx].showSubSection    = true;
            raw[subIdx].subSectionRowspan = span;
            for (let k = subIdx + 1; k < subIdx + span; k++) {
                raw[k].showSubSection    = false;
                raw[k].subSectionRowspan = 0;
            }
            subIdx += span;
        }

        return raw;
    }

    public toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
    }

    public openMaximizedView(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.isModalOpen = true;
        document.body.classList.add('drt-modal-open');
    }

    public closeMaximizedView(): void {
        this.isModalOpen = false;
        document.body.classList.remove('drt-modal-open');
    }

    public async downloadAsExcel(): Promise<void> {
        try {
            await this.downloadAsExcelInternal();
        } catch (err) {
            console.error('DRT Excel download failed:', err);
        }
    }

    public hasCitations(citations: CitationLink[] | null | undefined): boolean {
        return !!citations?.length;
    }

    /** Non-document sources (e.g. "ESRA", "DRT procedure") carry a label but no real link. */
    public isRealCitationUrl(url: string | null | undefined): boolean {
        const trimmed = (url || '').trim();
        return !!trimmed && trimmed !== '#';
    }

    public openCitation(url: string, event?: MouseEvent | KeyboardEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const trimmedUrl = (url || '').trim();
        if (!trimmedUrl) {
            return;
        }

        const openedWindow = window.open(trimmedUrl, '_blank', 'noopener,noreferrer');
        if (openedWindow) {
            return;
        }
    }

    public onCitationKeydown(url: string, event: KeyboardEvent): void {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        this.openCitation(url, event);
    }

    public formatCitationLabel(label: string, url?: string | null): string {
        const text = (label || '').trim();
        if (!text) {
            return '';
        }

        // Keep link targets encoded; decode only what users see.
        const candidate = text.includes('%') ? text : ((url || '').trim() === text ? text : '');
        if (!candidate) {
            return text;
        }

        try {
            return decodeURIComponent(candidate);
        } catch {
            return candidate.replace(/%20/gi, ' ');
        }
    }

    // Excel rejects hyperlinks that are not http(s) or exceed 2079 characters.
    private toSafeExcelUrl(url: string | null | undefined): string | null {
        try {
            const parsed = new URL((url || '').trim());
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
            return parsed.href.length <= 2079 ? parsed.href : null;
        } catch {
            return null;
        }
    }

    private async downloadAsExcelInternal(): Promise<void> {
        // Brand colours matching UI CSS variables.
        const PRIMARY    = 'FF0B2D71';
        const WHITE      = 'FFFFFFFF';
        const GRAY_LIGHT = 'FFDBDCDD';
        const ROW_ALT    = 'FFF4F6FB';
        const AI_BG      = 'FFEEF2FB';
        const BORDER     = 'FFDBDCDD';

        const cellBorder = (): Partial<ExcelJS.Borders> => ({
            top:    { style: 'thin', color: { argb: BORDER } },
            bottom: { style: 'thin', color: { argb: BORDER } },
            left:   { style: 'thin', color: { argb: BORDER } },
            right:  { style: 'thin', color: { argb: BORDER } }
        });

        const applyHeaderStyle = (cell: ExcelJS.Cell, bold = true): void => {
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: PRIMARY } };
            cell.font      = { bold, color: { argb: WHITE }, size: 8, name: 'Calibri' };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border    = cellBorder();
        };

        const now = new Date();
        const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

        // ── SINGLE EWO DRT VIEW: 2-sheet workbook (template + styled table) ──
        if (this.dynamicColumns && this.documents && this.documents.length > 0) {
            const rawPayload = this.unwrapPayload(this.documents[0]?.drtParameters || {});

            // Helper: safely read .value from a nested path in the rawPayload
            const val = (...keys: string[]): string => {
                let current: any = rawPayload;
                for (const k of keys) {
                    if (!current || typeof current !== 'object') return '-';
                    // Case-insensitive key match
                    // '%' must stay distinct, else 'RT/PAUT %' collides with 'RT/PAUT'.
                    const norm = (s: string): string => s.toLowerCase().replace(/%/g, 'pct').replace(/[^a-z0-9]/g, '');
                    const found = Object.keys(current).find(ck => norm(ck) === norm(k));
                    if (!found) return '-';
                    current = current[found];
                }
                if (current === null || current === undefined) return '-';
                if (typeof current === 'object' && 'value' in current) {
                    const v = current['value'];
                    if (v === null || v === undefined || v === '') return '-';
                    if (Array.isArray(v)) return v.filter(Boolean).join(', ') || '-';
                    return String(v);
                }
                if (Array.isArray(current)) return current.filter(Boolean).join(', ') || '-';
                return String(current);
            };

            // ── Sheet 1: Updated DRT Piping & Equipment template ─────────────
            const templateResponse = await fetch('/assets/drt-piping-equipment-template.xlsx');
            if (!templateResponse.ok) {
                console.error('Updated DRT template fetch failed:', templateResponse.status, templateResponse.statusText);
                return;
            }
            const templateBuffer = await templateResponse.arrayBuffer();
            const wb             = new ExcelJS.Workbook();
            await wb.xlsx.load(templateBuffer);
            const ws1 = wb.getWorksheet('Sheet1') || wb.worksheets[0];

            if (!ws1) {
                console.error('Updated DRT template does not contain a visible worksheet');
                return;
            }

            // Keep only the updated Sheet 1 template; Sheet 2 is added below exactly as before.
            wb.worksheets.filter(w => w.id !== ws1.id).forEach(w => wb.removeWorksheet(w.id));
            ws1.name = 'DRT Piping & Equipment';
            ws1.state = 'visible';

            // Template hatches the H37/H43/D43 header cells off the Bakeout cell ($H$1); key each off its own section.
            ((ws1 as any).conditionalFormattings as any[]).forEach((cf: any) => {
                const own = ['H37', 'H43', 'D43'].find(a => cf.ref === a);
                if (own) {
                    cf.rules.forEach((rule: any) => { rule.formulae = [`$${own[0]}$${own.slice(1)}="N/A"`]; });
                }
            });

            // ExcelJS reads the template's <strike val="0"/> / <i val="0"/> in conditional formats as true
            // (it ignores val), which strikes through and italicises the red Applicable Codes cell.
            ((ws1 as any).conditionalFormattings as any[]).forEach((cf: any) => {
                cf.rules.forEach((rule: any) => {
                    if (rule.style?.font) {
                        rule.style.font = { ...rule.style.font, strike: false, italic: false };
                    }
                });
            });

            // Re-add template pictures cleanly; round-tripping the original drawing makes Excel report a repair.
            const templateImages = ws1.getImages().map(img => ({
                buffer: wb.getImage(Number(img.imageId))?.buffer,
                extension: wb.getImage(Number(img.imageId))?.extension,
                col: (img.range.tl as any).nativeCol ?? img.range.tl.col,
                row: (img.range.tl as any).nativeRow ?? img.range.tl.row
            }));
            (ws1 as any)._media = [];
            templateImages.forEach(img => {
                if (!img.buffer || !img.extension) return;
                const id = wb.addImage({ buffer: img.buffer as any, extension: img.extension as any });
                ws1.addImage(id, { tl: { col: img.col, row: img.row }, ext: { width: 35, height: 33 }, editAs: 'oneCell' });
            });

            // ExcelJS drops the template's externalLinks part; leftover '[1]' formulas make Excel report corruption.
            ws1.eachRow(row => row.eachCell(cell => {
                const v: any = cell.value;
                if (v && typeof v === 'object' && typeof v.formula === 'string' && /\[\d+\]/.test(v.formula)) {
                    const res = v.result;
                    cell.value = (typeof res === 'string' || typeof res === 'number') ? res : '';
                }
            }));
            ws1.views = [{ state: 'normal', showGridLines: false, zoomScale: 85 }];
            ws1.pageSetup = {
                ...ws1.pageSetup,
                printArea: 'A1:H50',
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                orientation: 'landscape',
                paperSize: 9,
                horizontalCentered: true,
                verticalCentered: false,
                margins: {
                    left: 0.2,
                    right: 0.2,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0.1,
                    footer: 0.1
                }
            };

            // Empty values must clear the cell, otherwise the template's sample data survives and contradicts the DRT Table sheet.
            const touched = new Set<string>(['A16', 'A39', 'D43', 'E15']);
            const setCell = (addr: string, value: string): void => {
                touched.add(addr);
                ws1.getCell(addr).value = value && value !== '-' ? value : null;
            };

            // Process Information
            setCell('B2', val('Process Information', 'Service/process'));
            setCell('D2', val('Process Information', 'API 570 pipe class'));
            setCell('B3', val('Process Information', 'Environmental SCC'));
            setCell('D3', val('Process Information', 'Sour'));

            // Bakeout Information
            setCell('H1', val('Bakeout Information', 'is_required?'));
            setCell('F2', val('Bakeout Information', 'Temperature'));
            setCell('H2', val('Bakeout Information', 'Type'));
            setCell('F3', val('Bakeout Information', 'Duration'));
            setCell('E4', val('Bakeout Information', 'Notes'));

            // Welding Information
            // Backend renames 'value 1'/'value 2' to 'P-number 1'/'P-number 2' after resolving.
            const pNo = (n: 1 | 2): string => {
                const renamed = val('Welding Information', 'Metallurgy', `P-number ${n}`);
                return renamed !== '-' ? renamed : val('Welding Information', 'Metallurgy', `value ${n}`);
            };
            setCell('B8', pNo(1));
            setCell('D8', pNo(2));
            // A16:D20 is a merged block whose template text is a static note.
            const weldNotes = val('Welding Information', 'Notes');
            ws1.getCell('A16').value = weldNotes !== '-' ? `Notes:\n${weldNotes}` : 'Notes:';
            setCell('B9',  val('Welding Information', 'Preheat & Interpass Temperatures', 'Min preheat (degree Fahrenheit)'));
            setCell('D9',  val('Welding Information', 'Preheat & Interpass Temperatures', 'Maximum Interpass Temperature (degree Fahrenheit)'));
            setCell('B10', val('Welding Information', 'Preheat & Interpass Temperatures', 'If pre-heat is interrupted >3min'));

            // DHT / ISR
            setCell('H9',  val('DHT/ISR', 'is_required?'));
            setCell('F10', val('DHT/ISR', 'Dehydrogenation Heat Treatment (DHT)'));
            setCell('F13', val('DHT/ISR', 'Intermediate Stress Relief (ISR)'));
            // Template note lists both materials; keep only the one matching the P-number.
            const pNums = `${pNo(1)} ${pNo(2)}`.toUpperCase();
            const dhtNoteCell = ws1.getCell('E15');
            const dhtRequired = val('DHT/ISR', 'is_required?').trim().toUpperCase();
            if (dhtRequired === 'N/A' || dhtRequired === 'NOT REQUIRED') {
                dhtNoteCell.value = 'Notes:';
            } else if (typeof dhtNoteCell.value === 'string') {
                const hasA = /P5A/.test(pNums);
                const hasB = /P5B/.test(pNums);
                const dropMarker = hasA && hasB ? '' : hasB ? 'P5A material' : hasA ? 'P5B material' : '';
                if (dropMarker) {
                    dhtNoteCell.value = dhtNoteCell.value
                        .split(/\r?\n/)
                        .filter(line => !line.includes(dropMarker))
                        .join('\n');
                }
            }

            // Post-weld Heat Treatment (PWHT)
            setCell('H19', val('Post-weld Heat Treatment (PWHT)', 'is_required?'));
            setCell('F20', val('Post-weld Heat Treatment (PWHT)', 'Temperature (degree Fahrenheit)'));
            setCell('H20', val('Post-weld Heat Treatment (PWHT)', 'Duration'));
            setCell('F22', val('Post-weld Heat Treatment (PWHT)', 'Heating rates').replace(/^\s*heat(ing)?\s*rates?\s*:\s*/i, ''));
            setCell('F25', val('Post-weld Heat Treatment (PWHT)', 'Cooling rates').replace(/^\s*cool(ing)?\s*rates?\s*:\s*/i, ''));

            // NDE
            setCell('B22', val('NDE', 'Hydrotest pressure (psig)'));
            setCell('D22', val('NDE', 'Hydrotest Fluid'));
            setCell('B23', val('NDE', 'PMI'));
            setCell('D23', val('NDE', 'MDMT/MPT(°F)'));
            setCell('B24', val('NDE', 'RT/PAUT'));
            setCell('C24', val('NDE', 'RT/PAUT %'));
            setCell('B25', val('NDE', 'PT/MT'));
            setCell('C25', val('NDE', 'PT/MT %'));
            // Template styles PT/MT cells differently from RT/PAUT; match them so font size and centering agree.
            for (const col of ['B', 'C']) {
                const src = ws1.getCell(`${col}24`);
                const dst = ws1.getCell(`${col}25`);
                dst.font = { ...src.font };
                dst.alignment = { ...src.alignment };
            }
            setCell('B27', val('NDE', 'Ferrite Test'));
            setCell('B28', val('NDE', 'Hardness Test (BHN)'));
            const hardnessPct = val('NDE', 'Hardness Test (BHN) %').trim();
            setCell('C28', /^\d+(\.\d+)?$/.test(hardnessPct) ? `${hardnessPct}%` : hardnessPct);
            setCell('B32', val('NDE', 'Hydrotest Waived'));
            setCell('B33', val('NDE', 'Welds with waived Hydro'));
            setCell('B34', val('NDE', 'NDE In-Lieu of Hydro'));
            // Clears the template's default hydrotest-waiver note lines, keeping the label.
            ws1.getCell('A39').value = 'Notes:';

            // Heat Tracing Information
            setCell('H31', val('Heat Tracing Information', 'is required'));
            setCell('E33', val('Heat Tracing Information', 'Line# / Description'));
            setCell('G33', val('Heat Tracing Information', 'Steam/Electric'));
            setCell('H33', val('Heat Tracing Information', 'Applicable Std DWG'));
            setCell('E35', val('Heat Tracing Information', 'Notes'));

            // Insulation Information
            setCell('H37', val('Insulation Information', 'is required'));
            setCell('E39', val('Insulation Information', 'Line# / Description'));
            setCell('G39', val('Insulation Information', 'Insulation #'));
            setCell('H39', val('Insulation Information', 'Applicable Std DWG'));

            // Labeling Information
            setCell('A45', val('Labeling Information', 'Line # / Description'));
            setCell('C45', val('Labeling Information', 'Letter Color'));
            setCell('D45', val('Labeling Information', 'Band Color'));
            // Labeling is user-filled, so an empty block stays un-hatched; hatch (D43 = "N/A") only on an explicit negative.
            const labelingReq = val('Labeling Information', 'is required').trim();
            const labelingEmpty = ['A45', 'C45', 'D45'].every(a => !ws1.getCell(a).value);
            const labelingNA = /^(n\/?a|no|not applicable|not required)$/i.test(labelingReq);
            ws1.getCell('D43').value = labelingNA && labelingEmpty ? 'N/A' : (labelingReq !== '-' ? labelingReq : null);

            // Coating Information
            setCell('H43', val('Coating Information', 'is required'));
            setCell('E45', val('Coating Information', 'Line# / Description'));
            setCell('G45', val('Coating Information', 'Coating System'));
            setCell('H45', val('Coating Information', 'Top Coat Color'));

            // Footer
            setCell('F49', val('Other Information', 'EWO number'));
            setCell('H49', val('Other Information', 'Applicable Codes'));
            setCell('F50', val('Process Information', 'Pipe spec'));

            // Wrap every written cell and grow its row so long values are not clipped by the template's fixed cell size.
            const merges: Array<{ r1: number; c1: number; r2: number; c2: number }> = [];
            for (const m of ((ws1.model as any).merges ?? []) as string[]) {
                const [a, b] = m.split(':');
                const ca = ws1.getCell(a);
                const cb = ws1.getCell(b ?? a);
                merges.push({ r1: Number(ca.row), c1: Number(ca.col), r2: Number(cb.row), c2: Number(cb.col) });
            }
            const rowHeight = (r: number): number => ws1.getRow(r).height ?? 15;
            const extraByRow = new Map<number, number>();
            touched.forEach(addr => {
                const cell = ws1.getCell(addr);
                const text = cell.value == null ? '' : String(cell.value);
                if (!text) return;
                const row = Number(cell.row);
                const col = Number(cell.col);
                const mg = merges.find(m => row >= m.r1 && row <= m.r2 && col >= m.c1 && col <= m.c2);
                const [c1, c2, r1, r2] = mg ? [mg.c1, mg.c2, mg.r1, mg.r2] : [col, col, row, row];
                let width = 0;
                for (let c = c1; c <= c2; c++) width += ws1.getColumn(c).width ?? 8.43;
                const size = cell.font?.size ?? 11;
                const charsPerLine = Math.max(1, Math.floor((width * 11) / size * 0.95));
                const lines = text.split(/\r?\n/).reduce((n, l) => n + Math.max(1, Math.ceil(l.length / charsPerLine)), 0);
                const needed = lines * size * 1.35 + 2;
                let available = 0;
                for (let r = r1; r <= r2; r++) available += rowHeight(r);
                cell.alignment = { ...cell.alignment, wrapText: true, shrinkToFit: false };
                if (needed > available) {
                    extraByRow.set(r2, Math.max(extraByRow.get(r2) ?? 0, needed - available));
                }
            });
            extraByRow.forEach((extra, r) => { ws1.getRow(r).height = rowHeight(r) + extra; });

            // ── Sheet 2: Vertical DRT Table (Section | Sub-section | Field | Value | Citation | AI Reasoning | Conflict | Past EWO Suggestions) ──
            const ws2 = wb.addWorksheet('DRT Table', {
                views: [{ state: 'frozen', ySplit: 1 }]
            });

            // Column widths
            ws2.getColumn(1).width = 28;  // Section
            ws2.getColumn(2).width = 28;  // Sub-section
            ws2.getColumn(3).width = 30;  // Field
            ws2.getColumn(4).width = 55;  // Value
            ws2.getColumn(5).width = 42;  // Citation
            ws2.getColumn(6).width = 50;  // AI Reasoning
            ws2.getColumn(7).width = 45;  // Conflict
            ws2.getColumn(8).width = 45;  // Past EWO Suggestions

            // Header row
            const hRow = ws2.addRow(['Section', 'Sub-section', 'Field', 'Value', 'Citation', 'AI Reasoning', 'Validation / Conflicts', 'Past EWO Suggestions']);
            hRow.height = 20;
            [1, 2, 3, 4, 5, 6, 7, 8].forEach(c => applyHeaderStyle(hRow.getCell(c), true));

            // Data rows — with merged Section & Sub-section cells (mirrors rowspan in HTML)
            const vrows = this.allVerticalRows;
            const pendingMerges: Array<[number, number, number, number]> = [];
            vrows.forEach((vrow, idx) => {
                const excelRow = idx + 2;   // row 1 = header
                const citationText = this.getCitationExportText(vrow.citations);
                const pastEwoText  = this.getPastEwoExportText(vrow.pastEwoSuggestions);
                const dr = ws2.addRow([
                    vrow.showSection    ? vrow.section    : '',
                    vrow.showSubSection ? vrow.subSection : '',
                    vrow.fieldName,
                    vrow.value,
                    citationText,
                    vrow.aiReason,
                    [this.getValidationExportText(vrow.validationBadges), vrow.conflict].filter(Boolean).join('\n'),
                    pastEwoText
                ]);
                dr.height = 16;

                // Section cell (col 1) — write value only on first row of group, then merge
                const secCell = dr.getCell(1);
                secCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD0E4F5' } };
                secCell.font      = { bold: true, color: { argb: PRIMARY }, size: 8, name: 'Calibri' };
                secCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                secCell.border    = cellBorder();

                // Sub-section cell (col 2)
                const subCell = dr.getCell(2);
                subCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_LIGHT } };
                subCell.font      = { bold: false, color: { argb: PRIMARY }, size: 8, name: 'Calibri' };
                subCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                subCell.border    = cellBorder();

                // Field cell (col 3)
                const fldCell = dr.getCell(3);
                fldCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
                fldCell.font      = { bold: true, color: { argb: 'FF333333' }, size: 8, name: 'Calibri' };
                fldCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                fldCell.border    = cellBorder();

                // Value cell (col 4)
                const rowBg = idx % 2 === 0 ? WHITE : ROW_ALT;
                const valCell = dr.getCell(4);
                valCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                valCell.font      = { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                valCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                valCell.border    = cellBorder();

                // Citation cell (col 5)
                const citationCell = dr.getCell(5);
                citationCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                citationCell.font      = { size: 8, name: 'Calibri', color: { argb: 'FF1F5AA6' }, underline: true };
                citationCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                citationCell.border    = cellBorder();

                if (vrow.citations.length === 1) {
                    const [citation] = vrow.citations;
                    const safeCitationUrl = this.toSafeExcelUrl(citation.url);
                    if (this.isRealCitationUrl(citation.url) && safeCitationUrl) {
                        citationCell.value = {
                            text: citation.label,
                            hyperlink: safeCitationUrl
                        };
                    } else {
                        citationCell.value = citation.label;
                        citationCell.font = { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                    }
                } else if (!vrow.citations.length) {
                    citationCell.font = { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                } else {
                    citationCell.font = { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                }

                // AI Reasoning cell (col 6)
                const aiCell = dr.getCell(6);
                aiCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: AI_BG } };
                aiCell.font      = { italic: true, size: 7, name: 'Calibri', color: { argb: 'FF555555' } };
                aiCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                aiCell.border    = cellBorder();

                // Conflict cell (col 7)
                const conflictCell = dr.getCell(7);
                conflictCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                conflictCell.font      = vrow.conflict
                    ? { size: 8, name: 'Calibri', color: { argb: 'FFB42318' }, bold: true }
                    : { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                conflictCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                conflictCell.border    = cellBorder();

                // Past EWO Suggestions cell (col 8)
                const ewoCell = dr.getCell(8);
                ewoCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                ewoCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                ewoCell.border    = cellBorder();

                // Flatten: if there is exactly one suggestion with exactly one document,
                // render the value as a single hyperlink (mirrors Citation single-link behaviour).
                const flatDocs = vrow.pastEwoSuggestions.flatMap(s => s.documents);
                if (vrow.pastEwoSuggestions.length === 1 && flatDocs.length === 1) {
                    const onlySugg = vrow.pastEwoSuggestions[0];
                    const onlyDoc  = flatDocs[0];
                    const safeEwoUrl = this.toSafeExcelUrl(onlyDoc.url);
                    const ewoText = `${onlySugg.value} — ${onlyDoc.label}`;
                    ewoCell.value = safeEwoUrl ? { text: ewoText, hyperlink: safeEwoUrl } : ewoText;
                    ewoCell.font = safeEwoUrl
                        ? { size: 8, name: 'Calibri', color: { argb: 'FF1F5AA6' }, underline: true }
                        : { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                } else {
                    ewoCell.font = { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                }

                // Merging here would create future rows and push later addRow() calls past them, so defer.
                if (vrow.showSection && vrow.sectionRowspan > 1) {
                    pendingMerges.push([excelRow, 1, excelRow + vrow.sectionRowspan - 1, 1]);
                }
                if (vrow.showSubSection && vrow.subSectionRowspan > 1) {
                    pendingMerges.push([excelRow, 2, excelRow + vrow.subSectionRowspan - 1, 2]);
                }
            });
            pendingMerges.forEach(([r1, c1, r2, c2]) => ws2.mergeCells(r1, c1, r2, c2));

            applyDrtSheet1Dropdowns(wb, ws1, rawPayload['excel_dropdowns'] || {});

            // Download
            const buffer = await wb.xlsx.writeBuffer();
            const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url    = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href  = url;
            anchor.download = `DRT_Report_${timestamp}.xlsx`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return;
        }

        // ── COMPARISON / MULTI-DOC VIEW: scratch-built styled table ──────────
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('DRT Table', {
            views: [{ state: 'frozen', ySplit: 3, xSplit: 1 }]
        });

        // ── Column widths ──────────────────────────────────────────────────────
        ws.getColumn(1).width = 32;  // Document Title col
        for (let c = 2; c <= this.allColumns.length + 1; c++) {
            ws.getColumn(c).width = 22;
        }

        // ── Row 1 – top header groups ─────────────────────────────────────────
        const r1 = ws.addRow([]);
        r1.height = 22;

        // Document Title cell — will be merged vertically across rows 1-3 below
        const docCell = r1.getCell(1);
        docCell.value = 'Document Title';
        applyHeaderStyle(docCell, true);

        let col = 2;
        for (const group of this.allTopHeaderGroups) {
            const c = r1.getCell(col);
            c.value = group.label;
            applyHeaderStyle(c, true);
            if (group.span > 1) {
                ws.mergeCells(1, col, 1, col + group.span - 1);
                // Re-apply style to merged master cell
                applyHeaderStyle(ws.getCell(1, col), true);
            }
            col += group.span;
        }

        // ── Row 2 – sub header groups ─────────────────────────────────────────
        const r2 = ws.addRow([]);
        r2.height = 20;
        r2.getCell(1).border = cellBorder();

        col = 2;
        for (const group of this.allSubHeaderGroups) {
            const c = r2.getCell(col);
            c.value = group.label;
            applyHeaderStyle(c, true);
            if (group.span > 1) {
                ws.mergeCells(2, col, 2, col + group.span - 1);
                applyHeaderStyle(ws.getCell(2, col), true);
            }
            col += group.span;
        }

        // ── Row 3 – leaf labels ───────────────────────────────────────────────
        const r3 = ws.addRow([]);
        r3.height = 18;
        r3.getCell(1).border = cellBorder();

        let leafCol = 2;
        for (const column of this.allColumns) {
            const c = r3.getCell(leafCol);
            c.value = column.leafLabel || '';
            applyHeaderStyle(c, true);
            leafCol++;
        }

        // ── Merge Document Title cell vertically across rows 1-3 ─────────────
        ws.mergeCells(1, 1, 3, 1);
        applyHeaderStyle(ws.getCell(1, 1), true);

        // ── Data rows ────────────────────────────────────────────────────────
        this.allRows.forEach((row, idx) => {
            const excelRow = ws.addRow([row.documentTitle, ...row.values]);
            excelRow.height = 16;

            const docTitleCell = excelRow.getCell(1);
            docTitleCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_LIGHT } };
            docTitleCell.font      = { bold: true, color: { argb: PRIMARY }, size: 8, name: 'Calibri' };
            docTitleCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            docTitleCell.border    = cellBorder();

            const rowBg = idx % 2 === 0 ? WHITE : ROW_ALT;
            for (let c = 2; c <= row.values.length + 1; c++) {
                const dataCell = excelRow.getCell(c);
                dataCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                dataCell.font      = { size: 8, name: 'Calibri', color: { argb: 'FF3D3D3D' } };
                dataCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                dataCell.border    = cellBorder();
            }
        });

        // ── Trigger download ─────────────────────────────────────────────────
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `DRT_Table_${timestamp}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    }

    private unwrapPayload(raw: Record<string, any>): Record<string, any> {
        // If the payload has a 'data' key that is a plain object (not a leaf),
        // unwrap it so the actual DRT sections become top-level keys.
        if (raw['data'] && typeof raw['data'] === 'object' && !Array.isArray(raw['data'])) {
            return raw['data'];
        }
        return raw;
    }

    private buildTableDynamic(): void {
        const docs = (this.documents || []).filter(doc => doc?.documentType === 'EWO');
        const dynamicColumnMap = new Map<string, TableColumn>();

        docs.forEach((doc) => {
            const payload = this.unwrapPayload(doc.drtParameters || {});

            Object.entries(payload).forEach(([sectionKey, sectionValue]) => {
                if (!this.isValidSection(sectionKey) || sectionValue === null || sectionValue === undefined) {
                    return;
                }

                this.collectDynamicColumns(sectionValue, [sectionKey], dynamicColumnMap);
            });
        });

        const allColumns = Array.from(dynamicColumnMap.values());

        const allRows = docs.map((doc) => {
            const source = this.unwrapPayload(doc.drtParameters || {});
            return {
                documentTitle: this.getDocumentDisplayTitle(doc),
                values: allColumns.map((column) => this.getDisplayValue(source, column.keyPath)),
                aiReasons: allColumns.map((column) => this.getAiReasonValue(source, column.keyPath)),
                citations: allColumns.map((column) => this.getCitationEntries(source, column.keyPath)),
                conflicts: allColumns.map((column) => this.getConflictValue(source, column.keyPath)),
                validationBadges: allColumns.map((column) => this.getValidationBadges(source, column.keyPath)),
                pastEwoSuggestions: allColumns.map((column) => this.getPastEwoSuggestions(source, column.keyPath))
            };
        });

        this.allColumns = allColumns;
        this.allRows = allRows;
        this.allTopHeaderGroups = this.buildTopHeaderGroups(this.allColumns);
        this.allSubHeaderGroups = this.buildSubHeaderGroups(this.allColumns);

        // Filter out columns with no data
        const visibleColumnIndices: number[] = [];
        for (let colIndex = 0; colIndex < allColumns.length; colIndex++) {
            const hasData = allRows.some((row) => row.values[colIndex] !== '--' || row.validationBadges[colIndex].length > 0);
            if (hasData) {
                visibleColumnIndices.push(colIndex);
            }
        }

        this.columns = visibleColumnIndices.map(index => allColumns[index]);
        this.rows = allRows.map((row) => ({
            documentTitle: row.documentTitle,
            values: visibleColumnIndices.map(index => row.values[index]),
            aiReasons: visibleColumnIndices.map(index => row.aiReasons[index]),
            citations: visibleColumnIndices.map(index => row.citations[index]),
            conflicts: visibleColumnIndices.map(index => row.conflicts[index]),
            validationBadges: visibleColumnIndices.map(index => row.validationBadges[index]),
            pastEwoSuggestions: visibleColumnIndices.map(index => row.pastEwoSuggestions[index])
        }));

        this.topHeaderGroups = this.buildTopHeaderGroups(this.columns);
        this.subHeaderGroups = this.buildSubHeaderGroups(this.columns);
    }

    private isValidSection(key: string): boolean {
        const ignoredKeys = ['status', 'response', 'agent', 'thread_id', 'excel_file', 'excel_dropdowns', 'reasoning_log', 'llm_call_log', 'trace_data'];
        return !ignoredKeys.includes(key.toLowerCase());
    }

    private isIgnoredField(fieldName: string): boolean {
        const ignored = ['ai reason', 'ai_reason', 'citations', 'conflict', 'dropdown_opts'];
        return ignored.includes(fieldName.toLowerCase()) || VALIDATION_META_KEYS.has(fieldName.toLowerCase());
    }

    private collectDynamicColumns(value: any, path: string[], output: Map<string, TableColumn>): void {
        if (value === null || value === undefined) {
            return;
        }

        if (Array.isArray(value) || typeof value !== 'object' || this.isDynamicLeafNode(value)) {
            const column = this.buildDynamicColumn(path);
            output.set(column.keyPath.join('::'), column);
            return;
        }

        Object.entries(value).forEach(([childKey, childValue]) => {
            if (this.isIgnoredField(childKey)) {
                return;
            }

            this.collectDynamicColumns(childValue, [...path, childKey], output);
        });
    }

    private isDynamicLeafNode(value: Record<string, any>): boolean {
        if ('value' in value) {
            return true;
        }

        const meaningfulKeys = Object.keys(value).filter((key) => {
            if (DYNAMIC_META_KEYS.has(key.toLowerCase())) {
                return false;
            }

            return Number.isNaN(Number(key));
        });

        return meaningfulKeys.length === 0;
    }

    private buildDynamicColumn(path: string[]): TableColumn {
        const topLabel = this.prettyLabel(path[0] || 'Field');

        if (path.length === 1) {
            return {
                keyPath: path,
                topLabel,
                subLabel: 'Details',
                leafLabel: '',
                description: undefined
            };
        }

        if (path.length === 2) {
            return {
                keyPath: path,
                topLabel,
                subLabel: this.prettyLabel(path[1]),
                leafLabel: '',
                description: undefined
            };
        }

        return {
            keyPath: path,
            topLabel,
            subLabel: this.prettyLabel(path.slice(1, -1).join(' / ')),
            leafLabel: this.prettyLabel(path[path.length - 1]),
            description: undefined
        };
    }

    private compareColumnPaths(left: string[], right: string[]): number {
        const leftKey = left.map((item) => this.prettyLabel(item).toLowerCase()).join('::');
        const rightKey = right.map((item) => this.prettyLabel(item).toLowerCase()).join('::');
        return leftKey.localeCompare(rightKey);
    }

    private buildTable(): void {
        const docs = (this.documents || []).filter(doc => doc?.documentType === 'EWO');
        const allColumns = this.buildColumnsFromSchema();

        const allRows = docs.map((doc) => {
            const source = doc.drtParameters || {};

            return {
                documentTitle: this.getDocumentDisplayTitle(doc),
                values: allColumns.map((column) => this.getDisplayValue(source, column.keyPath)),
                aiReasons: allColumns.map((column) => this.getAiReasonValue(source, column.keyPath)),
                citations: allColumns.map((column) => this.getCitationEntries(source, column.keyPath)),
                conflicts: allColumns.map((column) => this.getConflictValue(source, column.keyPath)),
                validationBadges: allColumns.map((column) => this.getValidationBadges(source, column.keyPath)),
                pastEwoSuggestions: allColumns.map((column) => this.getPastEwoSuggestions(source, column.keyPath))
            };
        });

        this.allColumns = allColumns;
        this.allRows = allRows;
        this.allTopHeaderGroups = this.buildTopHeaderGroups(this.allColumns);
        this.allSubHeaderGroups = this.buildSubHeaderGroups(this.allColumns);

        // Filter out columns where all documents show '--' (no data)
        const visibleColumnIndices: number[] = [];
        for (let colIndex = 0; colIndex < allColumns.length; colIndex++) {
            const hasData = allRows.some((row) => row.values[colIndex] !== '--' || row.validationBadges[colIndex].length > 0);
            if (hasData) {
                visibleColumnIndices.push(colIndex);
            }
        }

        // Filter columns and row values to only include visible columns for inline view
        this.columns = visibleColumnIndices.map(index => allColumns[index]);
        this.rows = allRows.map((row) => ({
            documentTitle: row.documentTitle,
            values: visibleColumnIndices.map(index => row.values[index]),
            aiReasons: visibleColumnIndices.map(index => row.aiReasons[index]),
            citations: visibleColumnIndices.map(index => row.citations[index]),
            conflicts: visibleColumnIndices.map(index => row.conflicts[index]),
            validationBadges: visibleColumnIndices.map(index => row.validationBadges[index]),
            pastEwoSuggestions: visibleColumnIndices.map(index => row.pastEwoSuggestions[index])
        }));

        this.topHeaderGroups = this.buildTopHeaderGroups(this.columns);
        this.subHeaderGroups = this.buildSubHeaderGroups(this.columns);
    }

    private buildColumnsFromSchema(): TableColumn[] {
        const output: TableColumn[] = [];

        Object.entries(DRT_TABLE_SCHEMA.drt_fields).forEach(([fieldName, node]) => {
            this.collectColumns(node, [fieldName], output);
        });

        DRT_TABLE_SCHEMA.sql_context_fields.forEach((field) => {
            output.push({
                keyPath: ['SQL Context', field.field_name],
                topLabel: 'SQL Context',
                subLabel: this.prettyLabel(field.field_name),
                leafLabel: 'Value',
                description: field.description
            });
        });

        return output;
    }

    private collectColumns(node: DrtFieldNode, path: string[], output: TableColumn[]): void {
        if (node.is_simple_field) {
            output.push(this.buildColumn(path, node.description));
            return;
        }

        if (node.is_table && node.table_columns) {
            Object.entries(node.table_columns).forEach(([columnName, description]) => {
                output.push(this.buildColumn([...path, columnName], description));
            });
            return;
        }

        if (node.subfields) {
            Object.entries(node.subfields).forEach(([subfieldName, subNode]) => {
                this.collectColumns(subNode, [...path, subfieldName], output);
            });
            return;
        }

        output.push(this.buildColumn(path, node.description));
    }

    private buildColumn(path: string[], description?: string): TableColumn {
        const topLabel = this.prettyLabel(path[0] || 'Field');

        if (path.length === 1) {
            return {
                keyPath: path,
                topLabel,
                subLabel: 'Details',
                leafLabel: '',
                description
            };
        }

        if (path.length === 2) {
            return {
                keyPath: path,
                topLabel,
                subLabel: this.prettyLabel(path[1]),
                leafLabel: '',
                description
            };
        }

        return {
            keyPath: path,
            topLabel,
            subLabel: this.prettyLabel(path.slice(1, -1).join(' / ')),
            leafLabel: this.prettyLabel(path[path.length - 1]),
            description
        };
    }

    private buildTopHeaderGroups(columns: TableColumn[]): HeaderGroup[] {
        const groups: HeaderGroup[] = [];

        for (const column of columns) {
            const previous = groups[groups.length - 1];
            if (previous && previous.label === column.topLabel) {
                previous.span += 1;
            } else {
                groups.push({ label: column.topLabel, span: 1 });
            }
        }

        return groups;
    }

    private buildSubHeaderGroups(columns: TableColumn[]): HeaderGroup[] {
        const groups: HeaderGroup[] = [];

        for (const column of columns) {
            const groupedLabel = `${column.topLabel}::${column.subLabel}`;
            const previous = groups[groups.length - 1] as (HeaderGroup & { groupedLabel?: string }) | undefined;

            if (previous && previous.groupedLabel === groupedLabel) {
                previous.span += 1;
            } else {
                groups.push({
                    label: column.subLabel,
                    span: 1,
                    groupedLabel
                } as HeaderGroup & { groupedLabel: string });
            }
        }

        return groups.map(({ label, span }) => ({ label, span }));
    }

    private getDisplayValue(payload: Record<string, any>, keyPath: string[]): string {
        const value = this.resolveValue(payload, keyPath);

        if (value === null || value === undefined) {
            return '--';
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'null') {
                return '--';
            }
            return trimmed;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (Array.isArray(value)) {
            const rendered = value
                .map((item) => (typeof item === 'string' ? item.trim() : JSON.stringify(item)))
                .filter((item) => item && item !== 'null' && item !== 'None');

            const deduplicated = rendered.filter((item, index) => rendered.indexOf(item) === index);

            return deduplicated.length > 0 ? deduplicated.join(' | ') : '--';
        }

        if (typeof value === 'object') {
            if ('value' in value) {
                if (value.value !== null && value.value !== undefined) {
                    return this.getDisplayValue({ value: value.value }, ['value']);
                }
                return 'UNKNOWN';
            }
            return JSON.stringify(value);
        }

        return '--';
    }

    private resolveValue(payload: Record<string, any>, path: string[]): any {
        if (!payload || path.length === 0) {
            return null;
        }

        const [topLevel, ...rest] = path;
        let topLevelValue = this.getValueByNormalizedKey(payload, topLevel);

        if (topLevelValue === null && this.normalizeKey(topLevel) === 'serviceprocess') {
            for (const sectionKey of PROCESS_INFO_SECTION_KEYS) {
                const processInfo = this.getValueByNormalizedKey(payload, sectionKey);
                if (processInfo) {
                    topLevelValue = this.getValueByNormalizedKey(processInfo, 'Service/process');
                    if (topLevelValue !== null) {
                        break;
                    }
                }
            }
        }

        if (topLevelValue === null && this.normalizeKey(topLevel) === 'apipipeclass') {
            for (const sectionKey of PROCESS_INFO_SECTION_KEYS) {
                const processInfo = this.getValueByNormalizedKey(payload, sectionKey);
                if (processInfo) {
                    topLevelValue = this.getValueByNormalizedKey(processInfo, 'API pipe class');
                    if (topLevelValue !== null) {
                        break;
                    }
                }
            }
        }

        if (topLevelValue === null && this.normalizeKey(topLevel) === 'sour') {
            for (const sectionKey of PROCESS_INFO_SECTION_KEYS) {
                const processInfo = this.getValueByNormalizedKey(payload, sectionKey);
                if (processInfo) {
                    topLevelValue = this.getValueByNormalizedKey(processInfo, 'Sour');
                    if (topLevelValue !== null) {
                        break;
                    }
                }
            }
        }

        if (topLevelValue === null && this.normalizeKey(topLevel) === 'environmentalconditionsrestrictions') {
            for (const sectionKey of PROCESS_INFO_SECTION_KEYS) {
                const processInfo = this.getValueByNormalizedKey(payload, sectionKey);
                if (processInfo) {
                    topLevelValue = this.getValueByNormalizedKey(processInfo, 'Environmental SCC');
                    if (topLevelValue !== null) {
                        break;
                    }
                }
            }
        }

        if (rest.length === 0) {
            return topLevelValue;
        }

        return this.resolveNestedValue(topLevelValue, rest);
    }

    private resolveNestedValue(current: any, remainingPath: string[]): any {
        if (current === null || current === undefined) {
            return null;
        }

        if (remainingPath.length === 0) {
            return current;
        }

        const [currentKey, ...rest] = remainingPath;

        if (Array.isArray(current)) {
            if (rest.length === 0) {
                return current
                    .map((row) => this.getValueByNormalizedKey(row, currentKey))
                    .filter((item) => item !== null && item !== undefined);
            }

            return current
                .map((row) => this.resolveNestedValue(this.getValueByNormalizedKey(row, currentKey), rest))
                .flat()
                .filter((item) => item !== null && item !== undefined);
        }

        const parsedTable = this.tryParseProcedureTable(current);
        if (parsedTable) {
            return this.resolveNestedValue(parsedTable, remainingPath);
        }

        if (typeof current !== 'object') {
            return null;
        }

        const next = this.getValueByNormalizedKey(current, currentKey);
        return this.resolveNestedValue(next, rest);
    }

    private tryParseProcedureTable(value: any): any[] | null {
        if (Array.isArray(value)) {
            return value;
        }

        if (typeof value !== 'string') {
            return null;
        }

        const normalized = value
            .trim()
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');

        if (!normalized.startsWith('[') || !normalized.endsWith(']')) {
            return null;
        }

        const candidates = [
            normalized,
            normalized.replace(/'/g, '"')
        ];

        for (const candidate of candidates) {
            try {
                const parsed = JSON.parse(candidate);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
            } catch {
            }
        }

        return null;
    }

    private getValueByNormalizedKey(source: any, targetKey: string): any {
        if (!source || typeof source !== 'object') {
            return null;
        }

        if (Object.prototype.hasOwnProperty.call(source, targetKey)) {
            return source[targetKey];
        }

        const normalizedTarget = this.normalizeKey(targetKey);
        const aliases = KEY_ALIASES[normalizedTarget] || [];
        const lookupTargets = [normalizedTarget, ...aliases];

        const sourceKeys = Object.keys(source);

        let foundKey = sourceKeys.find((key) => {
            const normalizedKey = this.normalizeKey(key);
            return lookupTargets.includes(normalizedKey);
        });

        if (!foundKey) {
            foundKey = sourceKeys.find((key) => {
                const normalizedKey = this.normalizeKey(key);
                return lookupTargets.some((target) => normalizedKey.includes(target) || target.includes(normalizedKey));
            });
        }

        if (!foundKey) {
            return null;
        }

        return source[foundKey];
    }

    private normalizeKey(value: string): string {
        return value
            .replace(/Â/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
    }

    private prettyLabel(value: string): string {
        return value
            .replace(/Â/g, '')
            .replace(/_/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private resolveRawLeaf(payload: any, keyPath: string[]): any {
        let current = payload;
        for (const key of keyPath) {
            if (!current || typeof current !== 'object' || Array.isArray(current)) {
                return null;
            }
            // Exact key first
            if (Object.prototype.hasOwnProperty.call(current, key)) {
                current = current[key];
            } else {
                // Fall back to normalized key lookup
                current = this.getValueByNormalizedKey(current, key);
                if (current === null || current === undefined) {
                    return null;
                }
            }
        }
        return current;
    }

    private getFieldMetadata(payload: Record<string, any>, keyPath: string[]): Record<string, any> | null {
        const raw = this.resolveRawLeaf(payload, keyPath);
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            // Metallurgy P-number leaves are plain labels; their reasoning/citation sit on the parent Metallurgy object.
            const lastKey = keyPath[keyPath.length - 1] || '';
            if (keyPath.length > 1 && /^(p-?number|value)\s*\d$/i.test(lastKey.trim())) {
                const parent = this.resolveRawLeaf(payload, keyPath.slice(0, -1));
                if (parent && typeof parent === 'object' && !Array.isArray(parent)) {
                    return parent;
                }
            }
            return null;
        }
        return raw;
    }

    private getAiReasonValue(payload: Record<string, any>, keyPath: string[]): string {
        const raw = this.getFieldMetadata(payload, keyPath);
        if (!raw) {
            return '--';
        }
        const aiReasonKey = Object.keys(raw).find(
            (k) => k.toLowerCase() === 'ai reason' || k.toLowerCase() === 'ai_reason'
        );
        if (!aiReasonKey) {
            return '--';
        }
        const reason = raw[aiReasonKey];
        if (!reason || typeof reason !== 'string' || !reason.trim() ||
            reason.trim().toLowerCase() === 'null' || reason.trim().toLowerCase() === 'none') {
            return '--';
        }
        return reason.trim();
    }

    private getConflictValue(payload: Record<string, any>, keyPath: string[]): string {
        const raw = this.getFieldMetadata(payload, keyPath);
        if (!raw) {
            return '';
        }
        const conflictKey = Object.keys(raw).find((k) => k.toLowerCase() === 'conflict');
        if (!conflictKey) {
            return '';
        }
        const conflict = raw[conflictKey];
        if (!conflict || typeof conflict !== 'string' || !conflict.trim() ||
            conflict.trim().toLowerCase() === 'null' || conflict.trim().toLowerCase() === 'none') {
            return '';
        }
        return conflict.trim();
    }

    private getValidationBadges(payload: Record<string, any>, keyPath: string[]): ValidationBadge[] {
        const raw = this.getFieldMetadata(payload, keyPath);
        if (!raw) {
            return [];
        }
        const origin = raw['origin'] || raw['origins']?.[keyPath[keyPath.length - 1]];
        const badges: ValidationBadge[] = [];
        if (origin?.kind === 'user_input') {
            badges.push({ label: 'User override', tone: 'info', detail: 'User-supplied value; not automatic approval.' });
        }
        const statuses: Record<string, { label: string; tone: ValidationBadge['tone'] }> = {
            conflict: { label: 'Conflict', tone: 'error' },
            verification_unavailable: { label: "Couldn't verify", tone: 'warning' },
            missing_input: { label: 'Missing input', tone: 'warning' },
            needs_review: { label: 'Needs review', tone: 'warning' },
            policy_restriction: { label: 'Policy restriction', tone: 'error' },
            not_applicable: { label: 'Not applicable', tone: 'neutral' }
        };
        const status = raw['validation_status'] || (origin?.status === 'needs_validation' ? 'needs_review' : '');
        const entry = statuses[status];
        if (entry) {
            const detail = [
                this.getConflictValue(payload, keyPath),
                raw['verification']?.status === 'unavailable' ? String(raw['verification'].reason || '') : '',
                ...(Array.isArray(raw['validation_issues'])
                    ? raw['validation_issues'].map((issue: { message?: unknown }) => typeof issue?.message === 'string' ? issue.message : '')
                    : []),
                raw['normalization'] ? `Requested: ${raw['normalization'].requested}; effective: ${raw['normalization'].effective}` : '',
                raw['inactive_requested_value'] !== undefined ? `Retained inactive request: ${raw['inactive_requested_value']}` : ''
            ].filter(Boolean).join('\n');
            badges.push({ ...entry, detail: detail || entry.label });
        }
        if (raw['verification']?.status === 'unavailable' && status !== 'verification_unavailable') {
            badges.push({
                label: "Couldn't verify", tone: 'warning',
                detail: String(raw['verification'].reason || 'Reference verification unavailable.')
            });
        }
        return badges;
    }

    private getValidationExportText(badges: ValidationBadge[]): string {
        return badges.map(badge => badge.detail !== badge.label ? `${badge.label}: ${badge.detail}` : badge.label).join('\n');
    }

    private getCitationEntries(payload: Record<string, any>, keyPath: string[]): CitationLink[] {
        const raw = this.getFieldMetadata(payload, keyPath);
        if (!raw) {
            return [];
        }

        const citationKey = Object.keys(raw).find((key) => key.toLowerCase() === 'citations');
        if (!citationKey) {
            return [];
        }

        return this.normalizeCitations(raw[citationKey]);
    }

    private normalizeCitations(value: any): CitationLink[] {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return value.flatMap((entry) => {
                if (typeof entry === 'string') {
                    const trimmed = entry.trim();
                    return trimmed ? [{ label: trimmed, url: trimmed }] : [];
                }

                if (entry && typeof entry === 'object') {
                    const label = typeof entry.label === 'string' ? entry.label.trim() : '';
                    const url = typeof entry.url === 'string' ? entry.url.trim() : '';
                    return label && url ? [{ label, url }] : [];
                }

                return [];
            });
        }

        if (typeof value !== 'object') {
            return [];
        }

        return Object.entries(value)
            .map(([label, url]) => ({
                label: String(label || '').trim(),
                url: typeof url === 'string' ? url.trim() : ''
            }))
            .filter((citation) => !!citation.label && !!citation.url);
    }

    private getCitationExportText(citations: CitationLink[]): string {
        if (!citations.length) {
            return '';
        }

        if (citations.length === 1) {
            return citations[0].label;
        }

        return citations
            .map((citation) => `${citation.label}: ${citation.url}`)
            .join('\n');
    }

    private getPastEwoExportText(suggestions: PastEwoSuggestion[]): string {
        if (!suggestions || !suggestions.length) {
            return '';
        }

        return suggestions
            .map((suggestion) => {
                const docs = suggestion.documents
                    .map((doc) => `${doc.label}: ${doc.url}`)
                    .join('\n  ');
                return docs ? `${suggestion.value}\n  ${docs}` : suggestion.value;
            })
            .join('\n\n');
    }

    private getPastEwoSuggestions(payload: Record<string, any>, keyPath: string[]): PastEwoSuggestion[] {
        const raw = this.getFieldMetadata(payload, keyPath);
        if (!raw) {
            return [];
        }

        const suggestionKey = Object.keys(raw).find(
            (k) => k.toLowerCase() === 'past_ewo_suggestions' || k.toLowerCase() === 'past ewo suggestions'
        );
        if (!suggestionKey || !raw[suggestionKey] || typeof raw[suggestionKey] !== 'object') {
            return [];
        }

        const suggestions: PastEwoSuggestion[] = [];
        const suggestionData = raw[suggestionKey];

        Object.entries(suggestionData).forEach(([value, docs]) => {
            if (!value || !Array.isArray(docs)) {
                return;
            }
            const documents: CitationLink[] = docs
                .filter((doc: any) => doc && typeof doc === 'object' && doc.document_name && doc.blob_url)
                .map((doc: any) => ({
                    label: String(doc.document_name).trim(),
                    url: String(doc.blob_url).trim()
                }));

            if (documents.length) {
                suggestions.push({ value: value.trim(), documents });
            }
        });

        return suggestions;
    }

    private getDocumentDisplayTitle(document: ChatDocumentCard): string {
        if (document.title && document.title.trim()) {
            return document.title.trim();
        }

        if (document.ewoId && document.ewoId.trim()) {
            return document.ewoId.trim();
        }

        return 'Untitled Document';
    }
}
